import { createSignal, createEffect, Show, For, createMemo } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import {
  gold, goldPerEach, qty, relTime, freqLabel,
  qualityColor, esoIconUrl, ESO_ICON_FALLBACK,
} from '../lib/format.js';
import { pickUser, pickGuild } from '../lib/picks.js';
import ItemSearchInput from '../components/ItemSearchInput.jsx';
import FissalSuggestion from '../components/FissalSuggestion.jsx';
import SellingTips from '../components/SellingTips.jsx';
import wm from '../lib/wmStore.js';

/**
 * Item detail: searches for an item by name, then surfaces RF + TTC + ESO-Hub
 * averages, per-guild breakdown, recent sales, and Fissal pricing suggestion.
 */
export default function ItemDetail(props) {
  const [name, setName] = createSignal(props.settings?.name || props.settings?.item || '');
  const [nameDebounced, setNameDebounced] = createSignal(name());
  const [weekOffset, setWeekOffset] = createSignal(
    props.settings?.weekOffset !== undefined && props.settings?.weekOffset !== null && props.settings?.weekOffset !== ''
      ? Number(props.settings.weekOffset)
      : null
  );
  const [dateFrom, setDateFrom] = createSignal(props.settings?.dateFrom || '');
  const [dateTo,   setDateTo]   = createSignal(props.settings?.dateTo || '');

  function clearDateFilter() { setWeekOffset(null); setDateFrom(''); setDateTo(''); }

  createEffect(() => {
    const ext = String(props.settings?.name || props.settings?.item || '').trim();
    if (ext && ext !== nameDebounced()) {
      setName(ext);
      setNameDebounced(ext);
    }
  });

  createEffect(() => {
    const wid = props.widget?.id;
    const wtype = props.widget?.type || props.widget?.widget_type;
    const isStandaloneItemDetail = wtype === 'item-detail' || wtype === 'item-analysis';
    const nd = nameDebounced();
    if (!isStandaloneItemDetail && !nd) return;
    const wo = weekOffset();
    const df = dateFrom();
    const dt = dateTo();
    if (!wid) return;
    const cur = props.widget?.settings || {};
    if (
      (cur.name || '') !== (nd || '') ||
      (cur.weekOffset ?? null) !== (wo ?? null) ||
      (cur.dateFrom || '') !== (df || '') ||
      (cur.dateTo || '') !== (dt || '')
    ) {
      wm.setSettings(wid, {
        ...cur,
        name: nd || '',
        weekOffset: wo,
        dateFrom: df || '',
        dateTo: dt || '',
      });
    }
  });

  const params = createMemo(() => ({
    name:    nameDebounced(),
    quality: props.settings?.quality || null,
    trait:   props.settings?.trait   || null,
    guildId: props.settings?.guildId || null,
    weekOffset: weekOffset(),
    dateFrom:   weekOffset() === null && dateFrom() ? dateFrom() : null,
    dateTo:     weekOffset() === null && dateTo()   ? dateTo()   : null,
  }));
  const { data, loading, error } = useWidgetData('item-detail', params);
  const item = createMemo(() => data()?.item || null);
  const rf  = createMemo(() => item()?.rf || null);
  const ttc = createMemo(() => item()?.ttc || null);
  const esoHub = createMemo(() => item()?.esoHub || null);
  const guildBreakdown = createMemo(() => item()?.guildBreakdown || []);
  const fissal = createMemo(() => data()?.fissal || null);
  const recent = createMemo(() => (rf()?.sales || []).slice(0, 15));
  const resolvedIconUrl = createMemo(() => {
    const rawIcon = item()?.icon || rf()?.icon || null;
    const dispName = rf()?.actualItemName || item()?.name || name();
    return esoIconUrl(rawIcon, dispName);
  });

  return (
    <div class="v2-widget">
      <ItemSearchInput
        initialValue={name()}
        placeholder="Item name…"
        onValue={(rawVal, debounced) => {
          setName(rawVal);
          if (debounced !== undefined) setNameDebounced(debounced);
        }}
        onSubmit={(v) => {
          setNameDebounced(v);
        }}
      />
      <Show when={nameDebounced()} fallback={
        <div class="v2-empty" style={{ 'text-align': 'center', padding: 'var(--sp-3)' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)', color: 'var(--fissal)' }}>
            🔍 Item Detail
          </div>
          <div style={{ color: 'var(--t-green-dim)', 'margin-bottom': 'var(--sp-2)' }}>
            Enter an item name above to see Redfur Market, TTC, and ESO-Hub PC-NA averages,
            per-guild breakdown, recent sales, Fissal pricing tiers, and selling tips.
          </div>
          <div style={{ color: 'var(--t-green-dim)', 'font-size': 'var(--fs-xs)' }}>
            Tip: open the <b>⚙ Settings</b> menu to set permanent
            <b> Quality</b>, <b>Trait</b>, or <b>Guild</b> filters.
          </div>
        </div>
      }>
        <div class="v2-id__date-bar" style={{ display: 'flex', 'flex-wrap': 'wrap', gap: 'var(--sp-1)', 'align-items': 'center', 'margin-top': 'var(--sp-1)', 'font-size': 'var(--fs-xs)' }}>
          <button
            class="v2-btn-sm"
            style={weekOffset() === null && !dateFrom() && !dateTo() ? { color: 'var(--fissal)', 'border-color': 'var(--fissal)', background: 'rgba(0,255,136,0.08)' } : {}}
            onClick={clearDateFilter}
            title="Show all available historical sales (MM) and TTC snapshot data"
          >All time</button>
          <button
            class="v2-btn-sm"
            style={weekOffset() === 0 ? { color: 'var(--fissal)', 'border-color': 'var(--fissal)', background: 'rgba(0,255,136,0.08)' } : {}}
            onClick={() => { setWeekOffset(0); setDateFrom(''); setDateTo(''); }}
            title="Current ESO trade week (Tuesday reset)"
          >This week</button>
          <button
            class="v2-btn-sm"
            style={weekOffset() === 1 ? { color: 'var(--fissal)', 'border-color': 'var(--fissal)', background: 'rgba(0,255,136,0.08)' } : {}}
            onClick={() => { setWeekOffset(1); setDateFrom(''); setDateTo(''); }}
            title="Previous ESO trade week"
          >Last week</button>
          <span style={{ color: 'var(--t-green-dim)' }}>or</span>
          <input type="date" value={dateFrom()} style={{ 'font-size': 'var(--fs-xs)', background: 'var(--surface-sunken, #0a0a08)', color: 'var(--t-green)', border: '1px solid var(--window-edge)', 'border-radius': '4px', padding: '1px 4px' }}
            onInput={(e) => { setDateFrom(e.currentTarget.value); setWeekOffset(null); }}
            title="Custom range: from date (UTC)" />
          <span style={{ color: 'var(--t-green-dim)' }}>–</span>
          <input type="date" value={dateTo()} style={{ 'font-size': 'var(--fs-xs)', background: 'var(--surface-sunken, #0a0a08)', color: 'var(--t-green)', border: '1px solid var(--window-edge)', 'border-radius': '4px', padding: '1px 4px' }}
            onInput={(e) => { setDateTo(e.currentTarget.value); setWeekOffset(null); }}
            title="Custom range: to date (UTC, inclusive)" />
        </div>

        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <Show when={!loading() || item()} fallback={<div class="v2-widget-skel">Analyzing…</div>}>
            <Show when={rf() || ttc() || esoHub()?.hasData} fallback={<div class="v2-empty">No data for that name.</div>}>
              <div class="v2-widget__scroll v2-id__sales-scroll">
                <div class="v2-id__head" style={{ 'opacity': loading() ? 0.6 : 1, transition: 'opacity 120ms' }}>
                  <img class="v2-id__icon"
                    src={resolvedIconUrl()}
                    alt=""
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (img.src.endsWith('.png')) img.src = img.src.replace('.png', '.jpg');
                      else { img.onerror = null; img.src = ESO_ICON_FALLBACK; }
                    }} />
                  <div class="v2-id__head-text">
                    <div
                      class="v2-id__name"
                      style={{ '--item-quality': qualityColor(item()?.quality || ttc()?.topQuality) }}
                    >
                      {rf()?.actualItemName || ttc()?.itemName || item()?.name || name()}
                    </div>
                    <div class="v2-id__sub">
                      <Show when={item()?.quality || ttc()?.topQuality}>
                        <span class="v2-quality-dot" style={{ background: qualityColor(item()?.quality || ttc()?.topQuality) }} title={item()?.quality || ttc()?.topQuality} /> {item()?.quality || ttc()?.topQuality}
                      </Show>
                      <Show when={item()?.trait || ttc()?.topTrait}><span class="v2-trait-badge">{item()?.trait || ttc()?.topTrait}</span></Show>
                      <Show when={loading()}><span class="v2-dim">refreshing…</span></Show>
                    </div>
                  </div>
                </div>
                <div class="v2-stat-grid" style={{ 'opacity': loading() ? 0.6 : 1, transition: 'opacity 120ms' }}>
                  <Show when={rf() && rf().avgPrice}>
                    <div class="v2-stat v2-stat--gold">
                      <span class="v2-stat__label">MM avg</span>
                      <span class="v2-stat__value">{gold(rf().avgPrice)}</span>
                      <span class="v2-stat__sub">{qty(rf().saleCount)} sales · {qty(rf().volumeCount)} items</span>
                    </div>
                  </Show>
                  <Show when={ttc() && (ttc().suggestedPrice || ttc().avgPrice)}>
                    <div class="v2-stat v2-stat--cyan">
                      <span class="v2-stat__label" title="TTC Suggested (S) — TTC's recommended sale price. Falls back to avg listed (A) when not available.">TTC suggested</span>
                      <span class="v2-stat__value">{gold(ttc().suggestedPrice || ttc().avgPrice)}</span>
                      <span class="v2-stat__sub">
                        <Show when={ttc().saleAvg > 0}>sold avg {gold(ttc().saleAvg)} · </Show>
                        <Show when={ttc().suggestedPrice && ttc().avgPrice && ttc().avgPrice !== ttc().suggestedPrice}>listed {gold(ttc().avgPrice)} · </Show>
                        {qty(ttc().saleCount || 0)} sold
                      </span>
                    </div>
                  </Show>
                  <Show when={esoHub()?.hasData}>
                    <div class="v2-stat" style={{ 'border-left': '2px solid #d09cff' }}>
                      <span class="v2-stat__label" title="ESO-Hub PC-NA server-wide confirmed sales and listings.">ESO-Hub PC-NA</span>
                      <span class="v2-stat__value" style={{ color: '#d09cff' }}>
                        {gold(esoHub().avgSalePrice || esoHub().avgListingPrice)}
                      </span>
                      <span class="v2-stat__sub">
                        <Show when={esoHub().avgListingPrice > 0 && esoHub().avgSalePrice > 0}>
                          listed {gold(esoHub().avgListingPrice)} ·{' '}
                        </Show>
                        {qty(esoHub().totalSales || 0)} sold
                        <Show when={esoHub().sellThroughRatio > 0}>
                          {' · '}{Math.round(Math.min(1, esoHub().sellThroughRatio) * 100)}% ST
                        </Show>
                      </span>
                    </div>
                  </Show>
                  <Show when={rf() && rf().minPrice}>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Range</span>
                      <span class="v2-stat__value" style={{ 'font-size': 'var(--fs-md)' }}>
                        {gold(rf().minPrice)}<br />
                        <span style={{ 'font-size': '11px', color: 'var(--t-green-dim)' }}>↑ {gold(rf().maxPrice)}</span>
                      </span>
                    </div>
                  </Show>
                  <Show when={rf() && rf().sellFrequency}>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Frequency</span>
                      <span class="v2-stat__value" style={{ 'font-size': 'var(--fs-md)' }}>{freqLabel(rf().sellFrequency)}</span>
                    </div>
                  </Show>
                </div>
                <Show when={fissal() && fissal().fissalSuggested}>
                  <FissalSuggestion fissal={fissal()} />
                </Show>
                <SellingTips tips={rf()?.sellingTips} />
                <Show when={guildBreakdown().length}>
                  <div class="v2-section-h">By Guild ({guildBreakdown().length})</div>
                  <div>
                    <For each={guildBreakdown()}>{(g) => (
                      <div class="v2-sale-row">
                        <span class="v2-sale-row__main">
                          <b class="v2-pick v2-pick--guild" onClick={() => pickGuild(g.guildName)}>{g.guildName}</b>
                          <span class="v2-dim" style={{ 'margin-left': '6px', 'font-size': 'var(--fs-xs)' }}>
                            {qty(g.saleCount)} sales · {qty(g.volumeCount)} items
                            <Show when={g.sellFrequency}> · {freqLabel(g.sellFrequency)}</Show>
                          </span>
                        </span>
                        <span class="v2-sale-row__price">
                          {gold(g.avgPrice)}
                          <Show when={g.minPrice && g.maxPrice && g.minPrice !== g.maxPrice}>
                            <em class="v2-sale-row__per"> ({gold(g.minPrice)}–{gold(g.maxPrice)})</em>
                          </Show>
                        </span>
                      </div>
                    )}</For>
                  </div>
                </Show>
                <Show when={recent().length}>
                  <div class="v2-section-h">
                    Recent sales
                    <Show when={weekOffset() !== null}>
                      <span class="v2-dim" style={{ 'font-size': 'var(--fs-xs)', 'margin-left': 'var(--sp-1)' }}>
                        ({weekOffset() === 0 ? 'this week' : `${weekOffset()} week${weekOffset() === 1 ? '' : 's'} ago`})
                      </span>
                    </Show>
                    <Show when={weekOffset() === null && (dateFrom() || dateTo())}>
                      <span class="v2-dim" style={{ 'font-size': 'var(--fs-xs)', 'margin-left': 'var(--sp-1)' }}>
                        ({dateFrom() || '…'}–{dateTo() || '…'})
                      </span>
                    </Show>
                  </div>
                  <div>
                    <For each={recent()}>{(s) => (
                      <div class="v2-sale-row">
                        <span class="v2-sale-row__time">{relTime(s.timestamp)}</span>
                        <span class="v2-sale-row__main">
                          <Show when={s.sellerName || s.seller} fallback={<span class="v2-dim">(unknown)</span>}>
                            <b class="v2-pick v2-pick--user" onClick={() => pickUser(s.sellerName || s.seller)}>{s.sellerName || s.seller}</b>
                          </Show>
                          <Show when={s.quant > 1}><em> ×{s.quant}</em></Show>
                          <Show when={s.guildName}><em class="v2-sale-row__guild"> · <span class="v2-pick v2-pick--guild" onClick={() => pickGuild(s.guildName)}>{s.guildName}</span></em></Show>
                        </span>
                        <span class="v2-sale-row__price">
                          {gold(s.price)}
                          <Show when={s.quant > 1}>
                            <em class="v2-sale-row__per"> {goldPerEach(s.price, s.quant)}</em>
                          </Show>
                        </span>
                      </div>
                    )}</For>
                  </div>
                </Show>
              </div>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
